import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { VehicleGroup as AdsVehicleGroup } from 'src/app/ads/model/vehicle-group';
import { AdsVehicleGroupService } from 'src/app/ads/service/vehicle-group.service';
import { VehicleGroupsProvider as AdsVehicleGroupsProvider } from 'src/app/ads/service/vehicle-groups-provider';
import { DialogService } from 'src/app/common/dialog/dialog.service';
import { ToastService } from 'src/app/common/service/toast.service';
import { VehicleService } from 'src/app/management/service/vehicle.service';
import { Option } from 'src/app/settings/model/value-field';
import { UserSettingsService } from 'src/app/settings/service/user-settings.service';
import { FormEditorComponent } from 'src/app/shared/component/form-editor/form-editor.component';
import { FormFieldElement, FormFieldType } from 'src/app/shared/model/form-field-element';
import { isEqualById } from 'src/app/shared/util/comparator-util';
import { ENTITY_NOT_FOUND_ERROR_KEY, hasError } from 'src/app/shared/util/rest-service-util';
import { VehicleGroup } from '../../model/vehicle-group';
import { VehicleGroupType } from '../../model/vehicle-group-type';
import { VehicleGroupsProvider } from '../../service/vehicle-group-provider.service';
import { VehicleGroupService } from '../../service/vehicle-group.service';

@Component({
  selector: 'app-vehicle-group-dialog',
  templateUrl: './vehicle-group-dialog.component.html',
  styleUrls: ['./vehicle-group-dialog.component.scss'],
  providers: [
    VehicleGroupsProvider,
    AdsVehicleGroupService,
    {
      provide: AdsVehicleGroupsProvider,
      useFactory: (settings: UserSettingsService, groupService: AdsVehicleGroupService) =>
        settings.isAdsEnabled() ? new AdsVehicleGroupsProvider(groupService) : null,
      deps: [UserSettingsService, AdsVehicleGroupService]
    }
  ]
})
export class VehicleGroupDialogComponent implements OnInit, AfterViewInit {

  @ViewChild('vehicleGroupForm')
  vehicleGroupForm: FormEditorComponent;

  modes = Mode;
  mode: Mode = Mode.NONE;
  groupUpdated: boolean = false;

  form: FormGroup;
  formFields: FormFieldElement[];
  isEqualById = isEqualById;

  vehicleGroup: VehicleGroup;
  cancelText: string = 'back';

  vehiclesOptions: Option[];
  vehicleGroupTypeOptions: Option[];
  filteredVehicleGroups$: Observable<VehicleGroup[]>;
  vehicleGroups$: Observable<VehicleGroup[]>;
  private adsEnabled: boolean;

  constructor(private vehicleGroupService: VehicleGroupService, private adsVehicleGroupService: AdsVehicleGroupService,
    private toastService: ToastService, private vehicleService: VehicleService,
    private changeDetector: ChangeDetectorRef, private formBuilder: FormBuilder, private dialogService: DialogService,
    private vehicleGroupsProvider: VehicleGroupsProvider, @Optional() private adsVehicleGroupsProvider: AdsVehicleGroupsProvider,
    private dialogRef: MatDialogRef<VehicleGroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: { vehicleGroup?: VehicleGroup }
  ) {
    this.adsEnabled = adsVehicleGroupsProvider != null;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      id: new FormControl(null),
      name: new FormControl(null)
    });

    this.createVehiclesOptions();
    this.createVehicleGroupTypeOptions();
    this.initFormFields();
    this.loadVehicleGroupList();
    this.subscribeToInputChanges();

    if (this.data?.vehicleGroup) {
      this.vehicleGroup = this.data.vehicleGroup;
      this.mode = Mode.EDIT;
      this.changeDetector.detectChanges();
      this.vehicleGroupForm.editObject(this.convertVehicleGroupToFormField(this.vehicleGroup));
    }
  }

  ngAfterViewInit(): void {
    this.dialogRef.backdropClick().subscribe(() => {
      this.dialogRef.close({ updated: this.groupUpdated });
    });
  }

  displayFn(vehicleGroup: VehicleGroup): string {
    return vehicleGroup && vehicleGroup.name;
  }

  deselect(): void {
    this.form.get('name').setValue('');
  }

  removeVehicleGroup(vehicleGroup: VehicleGroup): void {
    this.dialogService.openConfirmationDialog('removeVehicleGroup')
      .afterClosed()
      .pipe(
        filter(result => result),
        switchMap(() => {
          if (vehicleGroup.groupType === VehicleGroupType.ADS) {
            return this.adsVehicleGroupService.deleteVehicleGroup(vehicleGroup.id);
          } else {
            return this.vehicleGroupService.deleteVehicleGroup(vehicleGroup.id);
          }
        }),
        catchError(error => {
          console.error(error);
          this.toastService.showErrorToast('vehicleGroupRemoveError');
          return of();
        })
      ).subscribe(() => {
        this.toastService.showInfoToast('vehicleGroupRemoved');
        this.updateForm(true);
        this.groupUpdated = true;
      });
  }

  vehicleGroupEdit(event: MatAutocompleteSelectedEvent): void {
    this.vehicleGroup = event.option.value;
    if (this.vehicleGroup) {
      this.mode = Mode.EDIT;
      this.changeDetector.detectChanges();
      this.vehicleGroupForm.editObject(this.convertVehicleGroupToFormField(this.vehicleGroup));
    } else {
      this.vehicleGroupForm.new();
    }
  }

  saveVehicleGroup(data: any): void {
    const vehicleGroup: VehicleGroup = this.convertFormFieldsToVehicleGroup(data);
    (this.vehicleGroup && vehicleGroup.groupType !== this.vehicleGroup.groupType ?
      this.quietRemove(this.vehicleGroup).pipe(tap(() => vehicleGroup.id = null)) :
      of(null)).pipe(tap(() => {
        if (vehicleGroup.groupType === VehicleGroupType.ADS) {
          this.saveAdsVehicleGroup(vehicleGroup);
        } else {
          this.saveStandardVehicleGroup(vehicleGroup);
        }
      })).subscribe();
  }

  private quietRemove(group: VehicleGroup): Observable<any> {
    return group.groupType === VehicleGroupType.ADS ?
      this.adsVehicleGroupService.deleteVehicleGroup(group.id) :
      this.vehicleGroupService.deleteVehicleGroup(group.id);
  }

  saveStandardVehicleGroup(vehicleGroup: VehicleGroup): void {
    this.vehicleGroupService.saveVehicleGroup(vehicleGroup)
      .subscribe(
        () => {
          this.toastService.showInfoToast('vehicleGroupSaved');
          this.updateForm(true);
          this.groupUpdated = true;
        }, error => {
          console.error(error);
          if (hasError(error, ENTITY_NOT_FOUND_ERROR_KEY))
            this.toastService.showErrorToast('vehicleGroupNotExists');
          else
            this.toastService.showErrorToast('vehicleGroupSaveError');
        });
  }

  saveAdsVehicleGroup(vehicleGroup: VehicleGroup): void {
    const adsVehicleGroup: AdsVehicleGroup = this.convertFormFieldsToAdsVehicleGroup(vehicleGroup);
    this.adsVehicleGroupService.saveVehicleGroup(adsVehicleGroup)
      .subscribe(
        () => {
          this.toastService.showInfoToast('vehicleGroupSaved');
          this.updateForm(true);
          this.groupUpdated = true;
        }, error => {
          console.error(error);
          if (hasError(error, ENTITY_NOT_FOUND_ERROR_KEY))
            this.toastService.showErrorToast('vehicleGroupNotExists');
          else
            this.toastService.showErrorToast('vehicleGroupSaveError');
        });
  }

  cancelAndRevert(): void {
    this.deselect();
    this.dialogRef.close({ updated: this.groupUpdated });
  }

  handleCancelClick(): void {
    this.deselect();
    this.mode = Mode.NONE;
  }

  handleAddNew(): void {
    this.deselect();
    this.mode = Mode.NEW;
    this.vehicleGroupForm?.resetForm();
  }

  private subscribeToInputChanges(): void {
    this.filteredVehicleGroups$ = this.form.get('name').valueChanges
      .pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : value.name),
        switchMap(name =>
          this.vehicleGroups$.pipe(
            map(groups => name ? this._filter(name, groups) : groups.slice()
            ))
        )
      );
  }

  private updateForm(reloadData: boolean = false): void {
    this.loadVehicleGroupList(reloadData);
    this.deselect();
    this.mode = Mode.NONE;
  }

  private convertVehicleGroupToFormField = (vehicleGroup: VehicleGroup): { id: number; name: string; sideNumbers: string[]; groupType: string } => ({
    id: vehicleGroup.id,
    name: vehicleGroup.name,
    sideNumbers: vehicleGroup.sideNumbers,
    groupType: vehicleGroup.groupType
  });

  private convertFormFieldsToVehicleGroup(data: any): VehicleGroup {
    return new VehicleGroup(data.id, data.name, data.sideNumbers, data.groupType);
  }

  private convertFormFieldsToAdsVehicleGroup(vehicleGroup: VehicleGroup): AdsVehicleGroup {
    return Object.assign(new AdsVehicleGroup(), {
      id: vehicleGroup.id,
      name: vehicleGroup.name,
      vehiclesSideNumbers: vehicleGroup.sideNumbers
    });
  }

  private createVehiclesOptions(): void {
    this.vehiclesOptions = [];
    this.vehicleService.fetchVehicles().pipe(shareReplay())
      .subscribe((vehicles) => vehicles.forEach(vehicle => this.vehiclesOptions.push({ key: vehicle.sideNumber, value: vehicle.sideNumber })));
  }

  private createVehicleGroupTypeOptions(): void {
    this.vehicleGroupTypeOptions = [];
    Object.values(VehicleGroupType)
      .filter(v => !(v === VehicleGroupType.ADS && !this.adsEnabled))
      .forEach(groupType => this.vehicleGroupTypeOptions.push({ key: groupType, value: groupType }));
  }

  private loadVehicleGroupList(reloadData: boolean = false): void {
    if (!reloadData) {
      this.vehicleGroups$ = this.vehicleGroupsProvider.groupsWithUpdates$
        .pipe(switchMap(
          (vehsGroups) =>
            this.adsEnabled ?
              this.adsVehicleGroupsProvider.groupsWithUpdates$.pipe(
                map(adsGroups => [
                  ...vehsGroups,
                  ...adsGroups.map(group => new VehicleGroup(group.id, group.name, group.vehiclesSideNumbers, VehicleGroupType.ADS))
                ])) :
              of(vehsGroups)
        ), catchError(error => {
          console.error(error);
          this.toastService.showErrorToast('vehicleGroupFetchError');
          return of([]);
        }));
    } else {
      if (this.adsEnabled)
        this.adsVehicleGroupsProvider.loadData().subscribe();
      this.vehicleGroupsProvider.loadData().subscribe();
    }
  }

  private _filter(name: string, groups: VehicleGroup[]): VehicleGroup[] {
    const filterValue = name.toLowerCase();
    return groups.filter(option => option.name.toLowerCase().includes(filterValue));
  }

  private initFormFields(): void {
    this.formFields = [
      {
        name: 'id',
        pk: true,
        fieldType: FormFieldType.HIDDEN_INPUT
      },
      {
        name: 'name',
        fieldType: FormFieldType.INPUT,
        formFieldType: 'text',
        maxLength: 50,
        validators: [
          {
            validator: Validators.required,
            validatorErrorName: 'required',
            errorKey: 'required'
          },
          {
            validator: Validators.maxLength(50),
            validatorErrorName: 'maxlength',
            errorKey: 'maxLength',
            errorParams: { value: 50 }
          }
        ]
      },
      {
        name: 'sideNumbers',
        fieldType: FormFieldType.MULTI_SELECT,
        options: this.vehiclesOptions,
        validators: [
          {
            validator: Validators.required,
            validatorErrorName: 'required',
            errorKey: 'required'
          }
        ]
      },
      {
        name: 'groupType',
        fieldType: FormFieldType.SELECT,
        options: this.vehicleGroupTypeOptions,
        validators: [
          {
            validator: Validators.required,
            validatorErrorName: 'required',
            errorKey: 'required'
          }
        ]
      }
    ];
  }
}


enum Mode {
  NONE, EDIT, NEW
}
